import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Pain subsection adapter
 * Handles pain assessment including FLACC scale components (7 fields)
 */
export class PainAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Pain assessment fields
    const painStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS
    )?.at(0)

    const painGoal = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL
    )?.at(0)

    // FLACC Scale components (Face, Legs, Activity, Cry, Consolability)
    const flaccScaleFace = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE
    )?.at(0)

    const flaccScaleLegs = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS
    )?.at(0)

    const flaccScaleActivity = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY
    )?.at(0)

    const flaccScaleCry = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY
    )?.at(0)

    const flaccScaleConsolability = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [DischargeFormField.PAIN_STATUS]: painStatus,
      [DischargeFormField.PAIN_GOAL]: painGoal,
      [DischargeFormField.PAIN_FLACC_SCALE_FACE]: flaccScaleFace,
      [DischargeFormField.PAIN_FLACC_SCALE_LEGS]: flaccScaleLegs,
      [DischargeFormField.PAIN_FLACC_SCALE_ACTIVITY]: flaccScaleActivity,
      [DischargeFormField.PAIN_FLACC_SCALE_CRY]: flaccScaleCry,
      [DischargeFormField.PAIN_FLACC_SCALE_CONSOLABILITY]: flaccScaleConsolability,
      [DischargeFormField.PAIN_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.PAIN_STATUS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS,
        value: this.getFormDataField(DischargeFormField.PAIN_STATUS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PAIN_GOAL)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL,
        value: this.getFormDataField(DischargeFormField.PAIN_GOAL)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PAIN_FLACC_SCALE_FACE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE,
        value: this.getFormDataField(DischargeFormField.PAIN_FLACC_SCALE_FACE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PAIN_FLACC_SCALE_LEGS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS,
        value: this.getFormDataField(DischargeFormField.PAIN_FLACC_SCALE_LEGS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PAIN_FLACC_SCALE_ACTIVITY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY,
        value: this.getFormDataField(DischargeFormField.PAIN_FLACC_SCALE_ACTIVITY)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PAIN_FLACC_SCALE_CRY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY,
        value: this.getFormDataField(DischargeFormField.PAIN_FLACC_SCALE_CRY)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PAIN_FLACC_SCALE_CONSOLABILITY)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY,
        value: this.getFormDataField(DischargeFormField.PAIN_FLACC_SCALE_CONSOLABILITY)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PAIN_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY,
        value: this.getFormDataField(DischargeFormField.PAIN_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
